import { isEqual, uniq } from 'es-toolkit';
import React, { useState } from 'react';
import { Box, Button, Divider, Tab, Tabs, Typography } from '@mui/material';

import {
  AnalysisSettings,
  ComponentDesignTimestamps,
  LayoutModel,
  LoadSettings,
  OrganizationPreset,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { assertExhaustiveSwitch, ReadonlyDictionary } from '@paragon/utilities';

import {
  setAnalysisSettingsForComponentDesignsFromObject,
  setLoadSettingsForComponentDesignsFromObject,
} from '../../../store/actions/settingsActions';
import { setComponentDesignTimestamps } from '../../../store/slices/componentDesignTimestampsSlice';
import { useAppDispatch } from '../../hooks';
import AnalysisSettingsControls from '../../interface/subpanels/settings/analysis/AnalysisSettingsControls';
import { DesignCodeField } from '../../interface/subpanels/settings/loading/DesignCodeField';
import {
  getUpdatedLoadSettingsForBuildingCode,
  LoadSettingsControls,
} from '../../interface/subpanels/settings/loading/LoadSettingsControls';
import { ApplyPresetDialog } from './ApplyPresetDialog';
import { ProjectHierarchyCheckboxTree } from './ProjectHierarchyCheckboxTree';
import { SettingsDialogTab } from './ProjectWideSettingsDialog';
import { SavePresetDialog } from './SavePresetDialog';

export function getDeterminateValueOrNull<T>(items: T[] | readonly T[]): T | null {
  if (items.length < 2) return items[0] ?? null;

  let isDeterminate = true;

  for (let i = 1; i < items.length; i++) {
    if (!isEqual(items[0], items[i])) {
      isDeterminate = false;
      break;
    }
  }

  return isDeterminate ? items[0] : null;
}

type UpdateFn<T> = (callback: (prevValue: T) => T) => void;

interface Props {
  readonly project: LayoutModel;

  readonly initialTab: SettingsDialogTab;
  readonly initiallySelectedComponentDesignGuids: readonly string[];

  readonly analysisSettingsPresets: OrganizationPreset<AnalysisSettings>[];
  readonly loadSettingsPresets: OrganizationPreset<LoadSettings>[];

  readonly loadSettingsByModelGuid: ReadonlyDictionary<LoadSettings>;
  readonly analysisSettingsByModelGuid: ReadonlyDictionary<AnalysisSettings>;

  readonly onLoadSettingsUpdated: UpdateFn<ReadonlyDictionary<LoadSettings>>;
  readonly onAnalysisSettingsUpdated: UpdateFn<ReadonlyDictionary<AnalysisSettings>>;

  readonly onPresetsInvalidated: (options?: { suppressSpinner?: boolean }) => Promise<void>;
}

export function LoadedProjectWideSettingsDialog({
  project,

  initialTab,
  initiallySelectedComponentDesignGuids,

  analysisSettingsPresets,
  loadSettingsPresets,

  loadSettingsByModelGuid,
  analysisSettingsByModelGuid,

  onLoadSettingsUpdated,
  onAnalysisSettingsUpdated,

  onPresetsInvalidated,
}: Props) {
  const dispatch = useAppDispatch();

  const [selectedModelGuids, setSelectedModelGuids] = useState(initiallySelectedComponentDesignGuids);

  const [editedLoadSettingsModelGuids, setEditedLoadSettingsModelGuids] = useState<readonly string[]>([]);
  const [editedAnalysisSettingsModelGuids, setEditedAnalysisSettingsModelGuids] = useState<readonly string[]>([]);

  const [activeSettingsType, setActiveSettingsType] = useState<SettingsDialogTab>(initialTab);
  const [didSavingFail, setDidSavingFail] = useState(false);

  const [showingSavePresetDialog, setShowingSavePresetDialog] = useState(false);
  const [showingApplyPresetDialog, setShowingApplyPresetDialog] = useState(false);

  const selectedLoadSettings =
    loadSettingsByModelGuid && typeof loadSettingsByModelGuid === 'object'
      ? selectedModelGuids.map((modelGuid) => loadSettingsByModelGuid![modelGuid])
      : [];
  const determinateSelectedLoadSettings = getDeterminateValueOrNull(selectedLoadSettings);
  const selectedLoadSettingsAreDeterminate = determinateSelectedLoadSettings !== null;

  const selectedAnalysisSettings =
    analysisSettingsByModelGuid && typeof analysisSettingsByModelGuid === 'object'
      ? selectedModelGuids.map((modelGuid) => analysisSettingsByModelGuid![modelGuid])
      : [];
  const determinateSelectedAnalysisSettings = getDeterminateValueOrNull(selectedAnalysisSettings);
  const selectedAnalysisSettingsAreDeterminate = determinateSelectedAnalysisSettings !== null;

  if (showingSavePresetDialog) {
    const settingsAreDeterminate =
      activeSettingsType === 'loadSettings'
        ? selectedLoadSettingsAreDeterminate
        : selectedAnalysisSettingsAreDeterminate;
    if (!settingsAreDeterminate) {
      throw new Error('Invalid state -- save preset dialog launched when selected models have inconsistent state.');
    }
  }

  function applyLoadSettingsUpdaterToSelectedModels(updater: (loadSettings: LoadSettings) => LoadSettings) {
    onLoadSettingsUpdated((prevState) => {
      const newState = { ...prevState };

      for (const modelGuid of selectedModelGuids) {
        newState[modelGuid] = updater(newState[modelGuid]);
      }

      return newState;
    });

    setEditedLoadSettingsModelGuids((prevState) => uniq([...prevState, ...selectedModelGuids]));
  }

  function applyLoadSettingsChangesToSelectedModels(changes: Partial<LoadSettings>) {
    applyLoadSettingsUpdaterToSelectedModels((loadSettings) => ({ ...loadSettings, ...changes }));
  }

  function applyAnalysisSettingsUpdaterToSelectedModels(
    updater: (analysisSettings: AnalysisSettings) => AnalysisSettings,
  ) {
    onAnalysisSettingsUpdated((prevState) => {
      const newState = { ...prevState };

      for (const modelGuid of selectedModelGuids) {
        newState[modelGuid] = updater(newState[modelGuid]);
      }

      return newState;
    });

    setEditedAnalysisSettingsModelGuids((prevState) => uniq([...prevState, ...selectedModelGuids]));
  }

  function renderDialogContents() {
    return (
      <Box sx={{ display: 'flex', maxHeight: '75vh' }}>
        <Box
          sx={[
            { minWidth: 250, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, marginRight: 1 },
            createScrollbarStyling,
          ]}
        >
          <ProjectHierarchyCheckboxTree
            project={project}
            selectedModelGuids={selectedModelGuids}
            setSelectedModelGuids={setSelectedModelGuids}
          />

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            disabled={
              selectedModelGuids.length === 0 ||
              (activeSettingsType === 'loadSettings' &&
                (loadSettingsByModelGuid == null ||
                  typeof loadSettingsByModelGuid !== 'object' ||
                  !selectedLoadSettingsAreDeterminate)) ||
              (activeSettingsType === 'analysisSettings' &&
                (analysisSettingsByModelGuid == null ||
                  typeof analysisSettingsByModelGuid !== 'object' ||
                  !selectedAnalysisSettingsAreDeterminate))
            }
            onClick={() => {
              setShowingSavePresetDialog(true);
            }}
          >
            Save As Preset
          </Button>

          {/* TODO: enable only when settings selected */}
          <Button
            variant="contained"
            disabled={
              selectedModelGuids.length === 0 ||
              (activeSettingsType === 'loadSettings' &&
                (loadSettingsByModelGuid == null || typeof loadSettingsByModelGuid !== 'object')) ||
              (activeSettingsType === 'analysisSettings' &&
                (analysisSettingsByModelGuid == null || typeof analysisSettingsByModelGuid !== 'object'))
            }
            onClick={() => void setShowingApplyPresetDialog(true)}
          >
            Apply Preset...
          </Button>
        </Box>

        <Divider orientation="vertical" flexItem />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 1,
            }}
          >
            <Tabs value={activeSettingsType} onChange={(_, tabValue) => void setActiveSettingsType(tabValue)}>
              <Tab value="loadSettings" label="Load Settings" />
              <Tab value="analysisSettings" label="Analysis Settings" />
            </Tabs>
          </Box>

          <Box sx={[{ flexGrow: 1, overflowY: 'auto' }, createScrollbarStyling]}>
            {(() => {
              switch (activeSettingsType) {
                case 'loadSettings':
                  return (
                    <LoadSettingsControls
                      isViewOnly={false}
                      showHeader={false}
                      showPresetControls={false}
                      loadSettingsArray={selectedModelGuids.map((modelGuid) => loadSettingsByModelGuid[modelGuid])}
                      onChange={applyLoadSettingsChangesToSelectedModels}
                      designCodeElement={
                        <DesignCodeField
                          isViewOnly={false}
                          loadSettingsWithDesignStandardsArray={selectedModelGuids.map((modelGuid) => {
                            if (!loadSettingsByModelGuid[modelGuid])
                              throw new Error(`Load settings not found for modelGuid: ${modelGuid}`);
                            if (!analysisSettingsByModelGuid[modelGuid])
                              throw new Error(`Analysis settings not found for modelGuid: ${modelGuid}`);

                            return {
                              loadSettings: loadSettingsByModelGuid[modelGuid],
                              designStandard: analysisSettingsByModelGuid[modelGuid].designStandard,
                            };
                          })}
                          applyDesignCodeChange={([buildingCode, designStandard]) => {
                            applyLoadSettingsUpdaterToSelectedModels((loadSettings) =>
                              getUpdatedLoadSettingsForBuildingCode(loadSettings, buildingCode),
                            );
                            applyAnalysisSettingsUpdaterToSelectedModels((analysisSettings) => ({
                              ...analysisSettings,
                              designStandard,
                            }));
                          }}
                        />
                      }
                    />
                  );

                case 'analysisSettings':
                  return (
                    <AnalysisSettingsControls
                      isViewOnly={false}
                      showHeader={false}
                      showPresetControls={false}
                      analysisSettingsArray={selectedModelGuids.map(
                        (modelGuid) => analysisSettingsByModelGuid[modelGuid],
                      )}
                      onChange={applyAnalysisSettingsUpdaterToSelectedModels}
                    />
                  );
              }
            })()}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Dialog
        variant="design"
        maxWidth="lg"
        insistent
        submitText="Save"
        onSubmit={async () => {
          setDidSavingFail(false);
          let timestamps: readonly ComponentDesignTimestamps[] = [];
          if (
            loadSettingsByModelGuid !== null &&
            typeof loadSettingsByModelGuid === 'object' &&
            editedLoadSettingsModelGuids.length > 0
          ) {
            const loadSettingsToSave = Object.fromEntries(
              Object.entries(loadSettingsByModelGuid).filter(([modelGuid]) =>
                editedLoadSettingsModelGuids.includes(modelGuid),
              ),
            );

            try {
              timestamps = await designService.settings.postBulkSetModelLoadSettingsInProject(
                project.guid,
                loadSettingsToSave,
              );
            } catch {
              setDidSavingFail(true);
              return;
            }

            dispatch(setLoadSettingsForComponentDesignsFromObject(loadSettingsToSave));
          }

          if (
            analysisSettingsByModelGuid !== null &&
            typeof analysisSettingsByModelGuid === 'object' &&
            editedAnalysisSettingsModelGuids.length > 0
          ) {
            const analysisSettingsToSave = Object.fromEntries(
              Object.entries(analysisSettingsByModelGuid).filter(([modelGuid]) =>
                editedAnalysisSettingsModelGuids.includes(modelGuid),
              ),
            );

            try {
              timestamps = await designService.settings.postBulkSetModelAnalysisSettingsInProject(
                project.guid,
                analysisSettingsToSave,
              );
            } catch {
              setDidSavingFail(true);
              return;
            }

            dispatch(setAnalysisSettingsForComponentDesignsFromObject(analysisSettingsToSave));
          }

          if (timestamps.length > 0) dispatch(setComponentDesignTimestamps({ timestamps: timestamps }));

          dispatch(closeCurrentPopup());
        }}
        submitDisabled={editedLoadSettingsModelGuids.length === 0 && editedAnalysisSettingsModelGuids.length === 0}
        onCancel="close"
        actionsNode={
          didSavingFail ? (
            <Typography color="red">Error saving settings. Try again or contact Paragon Support.</Typography>
          ) : null
        }
      >
        {renderDialogContents()}
      </Dialog>

      {showingSavePresetDialog ? (
        <SavePresetDialog
          settingsType={activeSettingsType}
          settings={
            activeSettingsType === 'loadSettings'
              ? determinateSelectedLoadSettings!
              : determinateSelectedAnalysisSettings!
          }
          onPresetsInvalidated={onPresetsInvalidated}
          onClose={() => setShowingSavePresetDialog(false)}
        />
      ) : showingApplyPresetDialog ? (
        <ApplyPresetDialog<AnalysisSettings | LoadSettings>
          presets={(() => {
            switch (activeSettingsType) {
              case 'analysisSettings':
                return analysisSettingsPresets;

              case 'loadSettings':
                return loadSettingsPresets;

              default:
                assertExhaustiveSwitch(activeSettingsType);
            }
          })()}
          onApplyPreset={(preset) => {
            setShowingApplyPresetDialog(false);

            switch (activeSettingsType) {
              case 'analysisSettings':
                const presetAnalysisSettings = preset.settings as AnalysisSettings;
                applyAnalysisSettingsUpdaterToSelectedModels((analysisSettings) => ({
                  ...presetAnalysisSettings,
                  designStandard: analysisSettings.designStandard,
                }));
                break;

              case 'loadSettings':
                const presetLoadSettings = preset.settings as LoadSettings;
                applyLoadSettingsUpdaterToSelectedModels((loadSettings) =>
                  getUpdatedLoadSettingsForBuildingCode(presetLoadSettings, loadSettings.buildingCode),
                );
                break;

              default:
                assertExhaustiveSwitch(activeSettingsType);
            }
          }}
          onClose={() => setShowingApplyPresetDialog(false)}
        />
      ) : null}
    </>
  );
}
